import { Link, useLocation } from "react-router-dom"
import { useState, useEffect } from "react"
import {Menu, X, Mail} from 'lucide-react'

const LOGO_URL = "https://static.lumi.new/4c/4c8f124f3e19139e5d5f616a0dcd86e1.png"

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const isLightPage = location.pathname === "/appeals"
  const showDarkNav = scrolled || isLightPage

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/services", label: "Services" },
    { path: "/careers", label: "Careers" },
    { path: "/appeals", label: "Appeals" },
    { path: "/contact", label: "Contact" },
  ]

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showDarkNav
          ? "bg-navy-950 backdrop-blur-xl shadow-2xl shadow-black/40 border-b border-navy-700"
          : "bg-transparent"
      }`}
      style={showDarkNav ? { backgroundColor: "rgba(5,10,20,0.97)" } : undefined}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex justify-between items-center h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group relative z-10">
            <img
              src={LOGO_URL}
              alt="Sentrio Security"
              className="h-12 sm:h-14 w-12 sm:w-14 rounded-full object-cover"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-5 py-2 text-sm font-medium tracking-wide transition-all duration-300 ${
                  location.pathname === link.path
                    ? "text-white"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {link.label}
                {location.pathname === link.path ? (
                  <span className="absolute bottom-0 left-5 right-5 h-px bg-slate-400" />
                ) : null}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href="mailto:info@sentriosecurity.com"
              className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>info@sentriosecurity.com</span>
            </a>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-white text-gray-900 text-sm font-semibold tracking-wide rounded hover:bg-slate-200 transition-all"
            >
              GET A QUOTE
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-300 hover:text-white relative z-10"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 backdrop-blur-xl transition-all duration-500 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{ top: 0, backgroundColor: "rgba(5,10,20,0.98)" }}
      >
        <div className="flex flex-col justify-center items-center min-h-screen gap-2 px-8">
          <img
            src={LOGO_URL}
            alt="Sentrio Security"
            className="h-20 w-20 rounded-full object-cover mb-8"
          />
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`text-2xl font-semibold tracking-wide py-3 transition-colors ${
                location.pathname === link.path
                  ? "text-white"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-8 flex flex-col items-center gap-4">
            <a
              href="mailto:info@sentriosecurity.com"
              className="flex items-center gap-2 text-slate-400 text-lg"
            >
              <Mail className="w-5 h-5" />
              info@sentriosecurity.com
            </a>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="px-10 py-4 bg-white text-gray-900 font-semibold text-lg tracking-wide rounded"
            >
              GET A QUOTE
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
